import {useEffect, useMemo, useState} from "react";

import {Button} from "@/components/ui/button";
import {Dialog, DialogContent, DialogTitle} from "@/components/ui/dialog";

type PanicConfirmOverlayProps = {
  open: boolean;
  expiresAt: string | null;
  onConfirm: () => void;
  onCancel: () => void;
  isSubmitting: boolean;
  dialTargets?: Array<{label: string; phone_number: string}> | null;
};

export function PanicConfirmOverlay({
  open,
  expiresAt,
  onConfirm,
  onCancel,
  isSubmitting,
  dialTargets,
}: PanicConfirmOverlayProps) {
  const [remainingSeconds, setRemainingSeconds] = useState(0);

  useEffect(() => {
    if (!open || !expiresAt) {
      setRemainingSeconds(0);
      return;
    }
    const run = () => {
      const next = Math.max(0, Math.ceil((new Date(expiresAt).getTime() - Date.now()) / 1000));
      setRemainingSeconds(next);
    };
    run();
    const timer = setInterval(run, 250);
    return () => clearInterval(timer);
  }, [open, expiresAt]);

  const hasDialTargets = (dialTargets?.length ?? 0) > 0;
  const progressLabel = useMemo(() => {
    if (!expiresAt) {
      return "Confirm panic alert";
    }
    return remainingSeconds > 0
      ? `Confirm within ${remainingSeconds}s`
      : "Confirmation window expired";
  }, [expiresAt, remainingSeconds]);

  return (
    <Dialog open={open} onOpenChange={() => {}}>
      <DialogContent className="sm:max-w-md" showCloseButton={false}>
        <DialogTitle className="text-base font-semibold">Panic alert confirmation</DialogTitle>
        <div className="flex flex-col gap-4 text-sm">
          <p className="text-muted-foreground">
            Hold started successfully. Sending only happens when you press confirm.
          </p>
          <p className="font-medium">{progressLabel}</p>

          {hasDialTargets ? (
            <div className="flex flex-col gap-2">
              <p className="text-xs text-muted-foreground">Quick dial targets</p>
              {dialTargets!.map((target) => (
                <a
                  key={`${target.label}-${target.phone_number}`}
                  href={`tel:${target.phone_number}`}
                  className="text-primary text-sm underline"
                >
                  {target.label}: {target.phone_number}
                </a>
              ))}
            </div>
          ) : null}

          <div className="flex items-center gap-2">
            <Button
              type="button"
              variant="destructive"
              className="flex-1"
              onClick={onConfirm}
              disabled={isSubmitting || remainingSeconds <= 0}
            >
              Send alert
            </Button>
            <Button type="button" variant="outline" className="flex-1" onClick={onCancel}>
              Cancel
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
